import React, { Fragment, useEffect } from 'react';
import { MenuItem, Select, TextField, FormControl, InputLabel, Button } from '@mui/material';
import FilterAccordian from '../filterAccordian';
import Breadcrumb from '../common/breadcrumb';
import { userRequest } from '../../auth/requestMethods';
import Email from '@mui/icons-material/Email';
import { toast } from 'react-toastify';
import CustomTable from '../tables/MuiDataGrid';
import DateRange from '../dateRange';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { Eye } from 'react-feather';
import dateFormat from '../../utils/dateFormat';
import { formatDate } from '../../helper/formatDate';

const STATUS_ENUM = ['DRAFT', 'QUEUED', 'RETRYING', 'SENT', 'FAILED'];

const Manage = ({ history }) => {
  useEffect(() => {
    document.title = 'PFSA | Emails';
  }, []);
  const [perPage, setPerPage] = React.useState(10);
  const [page, setPage] = React.useState(1);
  const [dataIsFetching, setDataIsFetching] = React.useState(true);
  const [filterOptions, setFilterOptions] = React.useState({
    emailTypes: [],
    users: [],
  });
  const [tableData, setTableData] = React.useState({
    count: 0,
    emails: [],
  });
  const [searchInput, setSearchInput] = React.useState({
    email_type_id: 'All',
    to: '', // partial match
    status: 'All',
    sent_by_user_id: 'All',
    from_date: '',
    to_date: '',
  });

  const [selectedDateRange, setSelectedDateRange] = React.useState({
    from_date: '',
    to_date: '',
  });

  React.useEffect(() => {
    setDataIsFetching((current) => {
      if (!current) return true;
    });

    userRequest()
      .get(`/emails?per_page=${perPage}&page=${page}&${getQueryString()}`)
      .then(({ data }) => {
        setTableData({
          emails: data.emails,
          count: data.total,
        });
        console.log(data);
        setDataIsFetching(false);
      })
      .catch(({ response }) => {
        console.log(response);
        setDataIsFetching(false);
      });
  }, [perPage, page]);

  React.useEffect(() => {
    bulkAxios(['/emails/types', '/users/names'])
      .then((all) => {
        console.log(all);
        setFilterOptions({
          emailTypes: all[0].emailTypes,
          users: all[1].users,
        });
      })
      .catch(({ response }) => console.log(response));
  }, []);

  function bulkAxios(urls = null) {
    if (!urls) return urls;

    let promises = urls.map((req) =>
      userRequest()
        .get(req)
        .then(({ data }) => {
          return data;
        })
        .catch((error) => {
          console.log(error);
          return null;
        })
    );

    return Promise.all(promises);
  }

  function getFilters() {
    const selectedFilters = Object.entries(searchInput)
      .map(([key, value]) => {
        if (value !== '') {
          if (key === 'email_type_id') {
            if (value === 'All') {
              return [];
            } else {
              return filterOptions.emailTypes.find((x) => x.id === value)?.display_name;
            }
          }
          if (key === 'to') {
            if (value === 'All') {
              return [];
            } else {
              return `To: ${value}`;
            }
          }
          if (key === 'status') {
            if (value === 'All') {
              return [];
            } else {
              return `Status: ${value}`;
            }
          }
          if (key === 'sent_by_user_id') {
            if (value === 'All') {
              return [];
            } else {
              return value === 0 ? 'System' : filterOptions.users.find((x) => x.id === value)?.user;
            }
          }
        }
      })
      .filter((x) => x !== undefined && x.length !== 0);

    if (selectedDateRange.from_date || selectedDateRange.to_date) {
      const dateRangeString = `Date Range: ${selectedDateRange.from_date || 'N/A'} - ${
        selectedDateRange.to_date || 'N/A'
      }`;
      selectedFilters.push(dateRangeString);
    }

    return selectedFilters;
  }

  function getQueryString() {
    let foundFilter = [];
    const search = { ...searchInput, ...selectedDateRange };
    search.created_range = `${search.from_date || ''},${search.to_date || ''}`;
    delete search.from_date;
    delete search.to_date;

    const queryString = Object.entries(search)
      .map(([key, value]) => {
        if (value !== '') {
          if (key === 'created_range') {
            let dates = value.split(',');
            if (dates.every((val) => val)) {
              foundFilter.push(key);
              return encodeURIComponent(key) + '=' + encodeURIComponent(value);
            }
          }
          if (key === 'status') {
            if (value === 'All') {
              key = '';
              value = '';
            }
          }
          if (key === 'email_type_id') {
            if (value === 'All') {
              key = '';
              value = '';
            }
          }
          if (key === 'sent_by_user_id') {
            if (value === 'All') {
              key = '';
              value = '';
            }
          }
          return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
        }
        return '';
      })
      .filter((Q) => !!Q)
      .join('&');

    return queryString;
  }

  const actionMethods = {
    link: {
      icon: <Eye />,
      toolTip: 'View',
      getLink: (row) => `/emails/view/${row.id}`,
    },
    custom: function ({ row }) {
      return (
        <>
          {row.has_email_send_failed && (
            <p style={{ color: 'blue', fontSize: '14px', cursor: 'pointer' }} onClick={onSend}>
              resend
            </p>
          )}
        </>
      );
    },
    width: 100,
  };
  const columns = [
    {
      field: 'email_type',
      headerName: 'Type',
      flex: 1,
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1,
    },
    {
      field: 'to',
      headerName: 'To',
      flex: 1,
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
    },
    {
      field: 'sending_attempt_count',
      headerName: 'Attempts',
      type: 'number',
      flex: 1,
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'created_date_time',
      headerName: 'Created',
      flex: 1,
      valueFormatter: ({ value }) => dateFormat(new Date(value?.slice(0, 10))),
    },
    {
      field: 'sent_date_time',
      headerName: 'Sent',
      flex: 1,
      valueFormatter: ({ value }) => dateFormat(new Date(value?.slice(0, 10))),
    },
    {
      field: 'resent_count',
      headerName: 'Resent',
      flex: 1,
      type: 'number',
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'attachments',
      headerName: 'Attachments',
      flex: 1,
      type: 'number',
      align: 'left',
      headerAlign: 'left',
    },
  ];

  const onSend = (email) => {
    toast.info('Sending...', {
      isLoading: true,
      position: 'bottom-center',
      autoClose: 5000,
      theme: 'colored',
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      toastId: 'create',
    });

    userRequest()
      .post('/emails/resend/' + email.id, email)
      .then((res) => {
        toast.dismiss('create');
        toast.success('Successfully Sent', {
          position: 'bottom-center',
          autoClose: 5000,
          closeOnClick: true,
          draggable: true,
        });
        // TODO: check if filters are met
        setTableData({
          ...tableData,
          emails: tableData.emails.map((em) => {
            if (em.id === email.id) {
              em.has_email_send_failed = true;
            }
            return em;
          }),
        });
      })
      .catch(({ response }) => {
        toast.dismiss('create');
        toast.error(response.data.message, {
          position: 'bottom-center',
          autoClose: 5000,
          closeOnClick: true,
          draggable: true,
        });
      });
  };

  const onChangeInput = ({ target: t }) => {
    setSearchInput((prev) => ({
      ...prev,
      ...selectedDateRange,
      [t.name]: t.value,
    }));

    // Fetch data immediately when filters change
    fetchData();
  };

  const onSearch = (e) => {
    e.preventDefault();

    const queryString = getQueryString();

    userRequest()
      .get(`/emails?per_page=${perPage}&page=${page}&${queryString}`)
      .then(({ data }) => {
        setTableData({
          count: data.total,
          emails: data.emails,
        });
      })
      .catch(({ response }) => {
        console.log(response);
      });
  };

  const fetchData = () => {
    setDataIsFetching(true);

    userRequest()
      .get(`/emails?per_page=${perPage}&page=${page}&${getQueryString()}`)
      .then(({ data }) => {
        setTableData({
          emails: data.emails,
          count: data.total,
        });
        setDataIsFetching(false);
      })
      .catch(({ response }) => {
        console.log(response);
        setDataIsFetching(false);
      });
  };

  React.useEffect(() => {
    fetchData();
  }, [perPage, page, searchInput, selectedDateRange]);

  return (
    <Fragment>
      <Breadcrumb icon={<Email />} title="Manage" parent="Emails" />

      <div className="container-fluid">
        <FilterAccordian filters={getFilters()}>
          <form className="theme-form">
            <div className="form-group row mx-0">
              <FormControl>
                <InputLabel id="demo-simple-select-helper-label">Type</InputLabel>
                <Select
                  name="email_type_id"
                  value={searchInput.email_type_id}
                  onChange={onChangeInput}
                  labelId="demo-simple-select-helper-label"
                  id="demo-simple-select-helper"
                  label="Type"
                  style={{
                    fontStyle: searchInput.email_type_id === 'All' ? 'italic' : 'normal',
                  }}
                >
                  <MenuItem value={'All'} style={{ fontStyle: 'italic' }}>
                    All
                  </MenuItem>
                  {filterOptions.emailTypes.map((ty) => (
                    <MenuItem key={ty.id} value={ty.id}>
                      {ty.display_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div className="form-group row mx-0">
              <TextField
                error={false}
                label="To"
                name="to"
                onChange={onChangeInput}
                variant="outlined"
                helperText={''}
              />
            </div>
            <div className="form-group row mx-0">
              <FormControl>
                <InputLabel>Sent By</InputLabel>
                <Select
                  name="sent_by_user_id"
                  value={searchInput.sent_by_user_id}
                  onChange={onChangeInput}
                  label="Sent By"
                  style={{
                    fontStyle: searchInput.sent_by_user_id === 'All' ? 'italic' : 'normal',
                  }}
                >
                  <MenuItem value={'All'} style={{ fontStyle: 'italic' }}>
                    All
                  </MenuItem>
                  <MenuItem value={0}>System</MenuItem>
                  {filterOptions.users.map((ty) => (
                    <MenuItem key={ty.id} value={ty.id}>
                      {ty.user}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div className="form-group row mx-0">
              <FormControl>
                <InputLabel id="demo-simple-select-helper-label">Status</InputLabel>
                <Select
                  name="status"
                  value={searchInput.status}
                  onChange={onChangeInput}
                  labelId="demo-simple-select-helper-label"
                  id="demo-simple-select-helper"
                  label="Status"
                  style={{
                    fontStyle: searchInput.status === 'All' ? 'italic' : 'normal',
                  }}
                >
                  <MenuItem value={'All'} style={{ fontStyle: 'italic' }}>
                    All
                  </MenuItem>
                  {STATUS_ENUM.map((val, i) => (
                    <MenuItem key={i} value={val}>
                      {val}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div className="form-group row mx-0">
              <p>Created Date Range</p>

              <DateRange
                views={['year', 'month', 'day']}
                from={selectedDateRange.from_date}
                to={selectedDateRange.to_date}
                fromOnChange={(val) => {
                  let value = !isNaN(Date.parse(val)) ? dateToFormattedString(new Date(val)) : '';
                  setSelectedDateRange((prev) => ({
                    ...prev,
                    from_date: value,
                  }));
                }}
                toOnChange={(val) => {
                  let value = !isNaN(Date.parse(val)) ? dateToFormattedString(new Date(val)) : '';
                  setSelectedDateRange((prev) => ({ ...prev, to_date: value }));
                }}
              />
            </div>
            {/* <div>
              <button className="btn btn-primary me-1" type="submit">
                Search
              </button>
            </div> */}
          </form>
        </FilterAccordian>
      </div>
      <br />
      <br />
      <div className="container-fluid">
        <CustomTable
          rows={tableData.emails}
          actions={actionMethods}
          columns={columns}
          setPageSize={setPerPage}
          setPage={setPage}
          rowCount={tableData.count}
          pageSize={perPage}
          loading={dataIsFetching}
          sortModel={[{ field: 'email_type', sort: 'asc' }]}
        />
      </div>
      <Dialog maxWidth="sm" TransitionProps={{ onEntered: () => {} }} open={false}>
        <DialogTitle>Success / Failure</DialogTitle>
        <DialogContent dividers>{`Email sent successfully / Email sending failed`}</DialogContent>
        <DialogActions>
          <Button onClick={() => {}}>OK</Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

function dateToFormattedString(d) {
  var yyyy = d.getFullYear().toString();
  var mm = (d.getMonth() + 1).toString(); // getMonth() is zero-based
  var dd = d.getDate().toString();

  return yyyy + '-' + (mm[1] ? mm : '0' + mm[0]) + '-' + (dd[1] ? dd : '0' + dd[0]);
}

export default Manage;
